import React, { useState, useEffect, useReducer, useCallback, useRef } from "react";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";
import { Alert } from "@material-ui/lab";
import {
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Chip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Typography,
  useMediaQuery,
} from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import SearchIcon from "@material-ui/icons/Search";
import AddIcon from "@material-ui/icons/Add";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import EditOutlinedIcon from "@material-ui/icons/EditOutlined";
import FileCopyOutlinedIcon from "@material-ui/icons/FileCopyOutlined";
import AssessmentOutlinedIcon from "@material-ui/icons/AssessmentOutlined";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import PauseIcon from "@material-ui/icons/Pause";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import SendIcon from "@material-ui/icons/Send";
import SpeedIcon from "@material-ui/icons/Speed";
import EventAvailableIcon from "@material-ui/icons/EventAvailable";
import moment from "moment";

import MainContainer from "../../components/MainContainer";
import Title from "../../components/Title";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import CampaignModal from "../../components/CampaignModal";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import { socketConnection } from "../../services/socket";
import {
  useCampaignStyles,
  KpiStrip,
  CampaignStatusPill,
  ProgressBar,
  STATUS_TABS,
  campaignNumbers,
  estimateSeconds,
  formatDuration,
  friendlyDateTime,
  fromNow,
  toneColor,
  parseCampaignSettings,
  MESSAGE_FIELDS,
  isCampaignStopped,
} from "./campaignShared";

const reducer = (state, action) => {
  switch (action.type) {
    case "LOAD": {
      const next = [...state];
      action.payload.forEach((c) => {
        const i = next.findIndex((x) => x.id === c.id);
        if (i !== -1) next[i] = c;
        else next.push(c);
      });
      return next;
    }
    case "UPSERT": {
      const { record, statusFilter } = action;
      const i = state.findIndex((x) => x.id === record.id);
      const fits = !statusFilter || record.status === statusFilter;
      if (!fits) return i === -1 ? state : state.filter((x) => x.id !== record.id);
      if (i !== -1) {
        const next = [...state];
        // o socket pode mandar o registro sem os números; mantém os que já havia
        next[i] = { ...next[i], ...record };
        return next;
      }
      return [record, ...state];
    }
    case "DELETE":
      return state.filter((x) => x.id !== action.payload);
    case "RESET":
      return [];
    default:
      return state;
  }
};

// O que falta para o rascunho poder ser enviado
const missingStep = (c) => {
  if (!c.contactListId) return "Falta escolher o público";
  if (!c.whatsappId) return "Falta escolher a conexão";
  if (!MESSAGE_FIELDS.some((f) => c[f] && String(c[f]).trim())) return "Falta escrever a mensagem";
  return null;
};

const Campaigns = () => {
  const classes = useCampaignStyles();
  const theme = useTheme();
  const history = useHistory();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [campaigns, dispatch] = useReducer(reducer, []);
  const [loading, setLoading] = useState(true);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [count, setCount] = useState(0);
  const [searchParam, setSearchParam] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [whatsappFilter, setWhatsappFilter] = useState("");
  const [whatsapps, setWhatsapps] = useState([]);
  const [summary, setSummary] = useState(null);
  // Saldo de créditos de campanha; null quando o plano não controla créditos
  // (o padrão), e aí o indicador simplesmente não aparece.
  const [credits, setCredits] = useState(null);
  // Se a empresa pode disparar agora (em período de teste, não pode).
  const [sending, setSending] = useState(null);
  const [pace, setPace] = useState(null);

  const [modal, setModal] = useState({ open: false, id: null });
  const [menu, setMenu] = useState({ anchor: null, campaign: null });
  const [confirm, setConfirm] = useState(null); // { kind, campaign }
  const [busyId, setBusyId] = useState(null);
  const [showDisclaimer, setShowDisclaimer] = useState(false);

  const statusRef = useRef(statusFilter);
  statusRef.current = statusFilter;

  useEffect(() => {
    try {
      if (!localStorage.getItem("campaignDisclaimerAccepted")) setShowDisclaimer(true);
    } catch (e) {
      // sem localStorage: não bloqueia a tela
    }
    api
      .get("/whatsapp", { params: { session: 0 } })
      .then(({ data }) => setWhatsapps(Array.isArray(data) ? data : []))
      .catch(() => {});
    api
      .get("/campaign-settings")
      .then(({ data }) => setPace(parseCampaignSettings(data)))
      .catch(() => {});
  }, []);

  // O saldo vem junto do resumo porque muda nos mesmos momentos: a cada evento
  // de campanha (mensagens saindo) e depois de cada ação na tela.
  const loadSummary = useCallback(() => {
    api
      .get("/campaigns/summary")
      .then(({ data }) => setSummary(data))
      .catch(() => {});
    api
      .get("/campaigns/credits/balance")
      .then(({ data }) => setCredits(data && data.enabled ? data : null))
      .catch(() => setCredits(null));
    // Junto do resumo: a marca de teste cai quando a fatura é paga, e a tela
    // passa a refletir isso no próximo carregamento do resumo.
    api
      .get("/campaigns/sending-status")
      .then(({ data }) => setSending(data))
      .catch(() => setSending(null));
  }, []);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam, statusFilter, whatsappFilter]);

  useEffect(() => {
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/campaigns/", {
          params: {
            searchParam,
            pageNumber,
            status: statusFilter || undefined,
            whatsappId: whatsappFilter || undefined,
          },
        });
        dispatch({ type: "LOAD", payload: data.records });
        setHasMore(data.hasMore);
        setCount(data.count);
      } catch (err) {
        toastError(err);
      }
      setLoading(false);
    }, 400);
    return () => clearTimeout(t);
  }, [searchParam, pageNumber, statusFilter, whatsappFilter]);

  // Atualização ao vivo (progresso, mudança de status)
  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    const socket = socketConnection({ companyId });
    let summaryTimer = null;
    socket.on(`company-${companyId}-campaign`, (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPSERT", record: data.record, statusFilter: statusRef.current });
      }
      if (data.action === "delete") {
        dispatch({ type: "DELETE", payload: +data.id });
      }
      clearTimeout(summaryTimer);
      summaryTimer = setTimeout(loadSummary, 1500);
    });
    return () => {
      clearTimeout(summaryTimer);
      socket.disconnect();
    };
  }, [loadSummary]);

  // O socket dos envios manda o registro sem os números de progresso:
  // busca o registro completo das campanhas em andamento de tempos em tempos.
  useEffect(() => {
    const running = campaigns.filter((c) => c.status === "EM_ANDAMENTO").map((c) => c.id);
    if (!running.length) return undefined;
    const t = setInterval(() => {
      running.forEach((id) =>
        api
          .get(`/campaigns/${id}`)
          .then(({ data }) => dispatch({ type: "UPSERT", record: data, statusFilter: statusRef.current }))
          .catch(() => {})
      );
    }, 15000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaigns.map((c) => `${c.id}:${c.status}`).join(",")]);

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) setPageNumber((p) => p + 1);
  };

  // ─── Ações ────────────────────────────────────────────────────────────────
  const run = async (campaign, fn, okMsg) => {
    setBusyId(campaign.id);
    try {
      await fn();
      if (okMsg) toast.success(okMsg);
      const { data } = await api.get(`/campaigns/${campaign.id}`);
      dispatch({ type: "UPSERT", record: data, statusFilter });
      loadSummary();
    } catch (err) {
      toastError(err);
    }
    setBusyId(null);
  };

  const pause = (c) => run(c, () => api.post(`/campaigns/${c.id}/cancel`), "Campanha pausada");
  const resume = (c) => run(c, () => api.post(`/campaigns/${c.id}/restart`), "Envio retomado");
  const sendNow = (c) => run(c, () => api.post(`/campaigns/${c.id}/send-now`), "Envio iniciado");

  const duplicate = async (c) => {
    setBusyId(c.id);
    try {
      const { data } = await api.post(`/campaigns/${c.id}/duplicate`);
      toast.success("Cópia criada como rascunho");
      loadSummary();
      setModal({ open: true, id: data.id });
    } catch (err) {
      toastError(err);
    }
    setBusyId(null);
  };

  const remove = async (c) => {
    try {
      await api.delete(`/campaigns/${c.id}`);
      toast.success(i18n.t("campaigns.toasts.deleted"));
      dispatch({ type: "DELETE", payload: c.id });
      loadSummary();
    } catch (err) {
      toastError(err);
    }
  };

  const openReport = (c) => history.push(`/campaign/${c.id}/report`);
  const openEdit = (c) => setModal({ open: true, id: c.id });

  const handleConfirm = () => {
    const { kind, campaign } = confirm;
    setConfirm(null);
    if (kind === "delete") remove(campaign);
    if (kind === "sendNow") sendNow(campaign);
    if (kind === "pause") pause(campaign);
  };

  // ─── Textos de cada linha ─────────────────────────────────────────────────
  const meta = (c) => {
    const parts = [];
    parts.push(c.contactList ? `Lista: ${c.contactList.name}` : "Sem público");
    parts.push(c.whatsapp ? `Conexão ${c.whatsapp.name}` : "Sem conexão");
    const variations = MESSAGE_FIELDS.filter((f) => c[f] && String(c[f]).trim()).length;
    if (variations > 1) parts.push(`${variations} variações`);
    if (c.confirmation) parts.push("pede confirmação");
    if (c.mediaPath) parts.push("com anexo");
    return parts.join(" · ");
  };

  const whenInfo = (c) => {
    const n = campaignNumbers(c);
    switch (c.status) {
      case "EM_ANDAMENTO": {
        if (c.scheduledAt && moment(c.scheduledAt).isAfter(moment())) {
          return { main: `Começa ${friendlyDateTime(c.scheduledAt).toLowerCase()}`, sub: fromNow(c.scheduledAt) };
        }
        const left = pace ? estimateSeconds(n.queued, pace) : null;
        return {
          main: c.scheduledAt ? `Desde ${friendlyDateTime(c.scheduledAt).toLowerCase()}` : "Enviando",
          sub: left !== null && n.queued > 0 ? `termina em ~${formatDuration(left)}` : "",
        };
      }
      case "PROGRAMADA":
        return { main: friendlyDateTime(c.scheduledAt), sub: fromNow(c.scheduledAt) };
      case "CANCELADA":
        return { main: `Pausada ${friendlyDateTime(c.updatedAt).toLowerCase()}`, sub: "" };
      case "PAUSADA_SEM_CREDITO":
        return { main: "Parou: créditos esgotados", sub: "recarregue e retome" };
      case "FINALIZADA": {
        const end = c.completedAt || c.updatedAt;
        const dur =
          c.scheduledAt && c.completedAt
            ? `durou ${formatDuration(moment(c.completedAt).diff(moment(c.scheduledAt), "seconds"))}`
            : "";
        return { main: friendlyDateTime(end), sub: dur };
      }
      default:
        return { main: "—", sub: "" };
    }
  };

  const progressCell = (c) => {
    const n = campaignNumbers(c);
    if (c.status === "INATIVA") {
      const miss = missingStep(c);
      return miss ? (
        <span className={classes.warnText}>{miss}</span>
      ) : (
        <span className={classes.subtle}>{n.valid} contatos com WhatsApp</span>
      );
    }
    if (c.status === "PROGRAMADA") {
      return <span className={classes.subtle}>{n.valid} contatos com WhatsApp</span>;
    }
    const tone =
      c.status === "PAUSADA_SEM_CREDITO"
        ? "red"
        : c.status === "CANCELADA"
        ? "amber"
        : c.status === "FINALIZADA"
        ? "purple"
        : "green";
    return (
      <div style={{ minWidth: 160 }}>
        <div className={classes.progressMeta}>
          <span>
            <b style={{ color: theme.palette.text.primary }}>{n.delivered}</b> de {n.valid}
            {c.status === "FINALIZADA" ? " entregues" : ""}
          </span>
          <span>{n.pct}%</span>
        </div>
        <ProgressBar classes={classes} segments={[{ pct: n.pct, color: toneColor(theme, tone) }]} />
      </div>
    );
  };

  // Botões principais da linha (o resto vai no menu ⋮)
  const primaryActions = (c) => {
    const busy = busyId === c.id;
    const btn = (label, onClick, cls, icon) => (
      <Button
        key={label}
        size="small"
        disabled={busy}
        className={`${classes.rowButton} ${cls}`}
        variant={cls === classes.rowButtonOutlined ? "outlined" : "text"}
        startIcon={icon}
        onClick={onClick}
      >
        {label}
      </Button>
    );
    switch (c.status) {
      case "EM_ANDAMENTO":
        return [
          btn("Pausar", () => setConfirm({ kind: "pause", campaign: c }), classes.tintAmber, <PauseIcon fontSize="small" />),
          btn("Relatório", () => openReport(c), classes.rowButtonOutlined),
        ];
      case "PROGRAMADA":
        return [
          btn("Enviar agora", () => setConfirm({ kind: "sendNow", campaign: c }), classes.tintBlue, <SendIcon fontSize="small" />),
          btn("Editar", () => openEdit(c), classes.rowButtonOutlined),
        ];
      case "CANCELADA":
      case "PAUSADA_SEM_CREDITO":
        return [
          btn("Retomar", () => resume(c), classes.tintGreen, <PlayArrowIcon fontSize="small" />),
          btn("Relatório", () => openReport(c), classes.rowButtonOutlined),
        ];
      case "FINALIZADA":
        return [
          btn("Duplicar", () => duplicate(c), classes.rowButtonOutlined),
          btn("Relatório", () => openReport(c), classes.rowButtonOutlined),
        ];
      default:
        return [btn("Continuar", () => openEdit(c), classes.tintBlue)];
    }
  };

  const menuItems = (c) => {
    if (!c) return [];
    const items = [];
    const editable = ["INATIVA", "PROGRAMADA"].includes(c.status) || isCampaignStopped(c.status);
    if (editable) items.push({ label: c.status === "INATIVA" ? "Continuar editando" : "Editar", icon: <EditOutlinedIcon fontSize="small" />, onClick: () => openEdit(c) });
    if (c.status !== "FINALIZADA") items.push({ label: "Duplicar", icon: <FileCopyOutlinedIcon fontSize="small" />, onClick: () => duplicate(c) });
    if (c.status !== "INATIVA") items.push({ label: "Ver relatório", icon: <AssessmentOutlinedIcon fontSize="small" />, onClick: () => openReport(c) });
    if (c.status !== "EM_ANDAMENTO")
      items.push({ label: "Excluir", danger: true, icon: <DeleteOutlineIcon fontSize="small" />, onClick: () => setConfirm({ kind: "delete", campaign: c }) });
    return items;
  };

  // ─── Topo ─────────────────────────────────────────────────────────────────
  const sc = summary?.statusCounts || {};
  const totalCampaigns = Object.values(sc).reduce((a, b) => a + b, 0);
  const confirmRate =
    summary && summary.confirmationRequestedThisMonth > 0
      ? `${Math.round((summary.confirmedThisMonth / summary.confirmationRequestedThisMonth) * 100)}%`
      : "—";
  const kpis = [
    {
      label: "Enviando agora",
      tone: "green",
      value: sc.EM_ANDAMENTO || 0,
      caption: summary ? `${summary.queuedMessages.toLocaleString("pt-BR")} mensagens na fila` : "",
    },
    {
      label: "Programadas",
      tone: "blue",
      value: sc.PROGRAMADA || 0,
      caption: summary?.nextScheduledAt ? `Próxima: ${friendlyDateTime(summary.nextScheduledAt).toLowerCase()}` : "Nenhuma agendada",
    },
    {
      label: "Entregues no mês",
      tone: "grey",
      value: summary ? summary.deliveredThisMonth.toLocaleString("pt-BR") : 0,
      caption: summary ? `em ${summary.campaignsThisMonth} ${summary.campaignsThisMonth === 1 ? "campanha" : "campanhas"}` : "",
    },
    {
      label: "Confirmações",
      tone: "amber",
      value: confirmRate,
      caption: "das que pediram confirmação no mês",
    },
    // Créditos: só quando o plano controla créditos. 1 crédito = 1 mensagem;
    // a cota do plano é usada primeiro e renova a cada ciclo, o avulso não expira.
    ...(credits
      ? [
          {
            label: "Créditos",
            tone: credits.available > 0 ? "green" : "red",
            colorValue: credits.available <= 0,
            value: credits.available.toLocaleString("pt-BR"),
            captionTone: credits.available <= 0 ? "red" : undefined,
            caption:
              credits.available <= 0
                ? "esgotados — campanhas em envio vão pausar"
                : credits.monthlyQuota > 0
                ? `cota: ${credits.quotaRemaining.toLocaleString("pt-BR")} de ${credits.monthlyQuota.toLocaleString("pt-BR")}` +
                  (credits.cycleResetAt ? ` até ${moment(credits.cycleResetAt).format("DD/MM")}` : "") +
                  ` · avulso: ${credits.extraBalance.toLocaleString("pt-BR")}`
                : `saldo avulso`,
          },
        ]
      : []),
  ];

  const emptyMessage = searchParam
    ? "Nenhuma campanha com esse nome"
    : statusFilter
    ? "Nenhuma campanha nesta situação"
    : "Você ainda não criou campanhas";

  const confirmTexts = {
    delete: {
      title: "Excluir campanha?",
      body: "A campanha e o histórico de envios serão apagados. Não é possível desfazer.",
      action: "Excluir",
      danger: true,
    },
    sendNow: {
      title: "Enviar agora?",
      body: "A campanha começa em alguns segundos, no ritmo de envio configurado, em vez de esperar o horário agendado.",
      action: "Enviar agora",
    },
    pause: {
      title: "Pausar o envio?",
      body: "As mensagens que ainda estão na fila não serão enviadas. Você pode retomar depois — quem já recebeu não recebe de novo.",
      action: "Pausar",
    },
  };

  return (
    <MainContainer>
      {/* Aviso de responsabilidade (uma vez por navegador) */}
      <Dialog open={showDisclaimer} onClose={() => {}} aria-labelledby="disclaimer-dialog">
        <DialogTitle id="disclaimer-dialog">{i18n.t("campaignsMessageAlert.disclaimer.title")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {i18n.t("campaignsMessageAlert.disclaimer.message")}
            <br />
            <br />
            <strong>{i18n.t("campaignsMessageAlert.disclaimer.responsibility")}</strong>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            color="primary"
            autoFocus
            onClick={() => {
              try {
                localStorage.setItem("campaignDisclaimerAccepted", moment().toISOString());
              } catch (e) {
                // ignora
              }
              setShowDisclaimer(false);
            }}
          >
            {i18n.t("campaignsMessageAlert.disclaimer.agree")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!confirm} onClose={() => setConfirm(null)} maxWidth="xs" fullWidth>
        {confirm && (
          <>
            <DialogTitle>{confirmTexts[confirm.kind].title}</DialogTitle>
            <DialogContent>
              <Typography variant="body2" style={{ fontWeight: 600, marginBottom: 8 }}>
                {confirm.campaign.name}
              </Typography>
              <Typography variant="body2" className={classes.muted}>
                {confirmTexts[confirm.kind].body}
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setConfirm(null)}>Voltar</Button>
              <Button
                onClick={handleConfirm}
                variant="contained"
                disableElevation
                color={confirmTexts[confirm.kind].danger ? "default" : "primary"}
                style={confirmTexts[confirm.kind].danger ? { backgroundColor: theme.palette.error.main, color: "#fff" } : undefined}
              >
                {confirmTexts[confirm.kind].action}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <CampaignModal
        open={modal.open}
        campaignId={modal.id}
        onClose={() => setModal({ open: false, id: null })}
        onSaved={(record) => {
          if (record) dispatch({ type: "UPSERT", record, statusFilter });
          loadSummary();
        }}
      />

      <Menu
        anchorEl={menu.anchor}
        open={!!menu.anchor}
        onClose={() => setMenu({ anchor: null, campaign: null })}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {isMobile &&
          menu.campaign &&
          (() => {
            const c = menu.campaign;
            const mobileMain = [];
            if (c.status === "EM_ANDAMENTO") mobileMain.push({ label: "Pausar", icon: <PauseIcon fontSize="small" />, onClick: () => setConfirm({ kind: "pause", campaign: c }) });
            if (isCampaignStopped(c.status)) mobileMain.push({ label: "Retomar", icon: <PlayArrowIcon fontSize="small" />, onClick: () => resume(c) });
            if (c.status === "PROGRAMADA") mobileMain.push({ label: "Enviar agora", icon: <SendIcon fontSize="small" />, onClick: () => setConfirm({ kind: "sendNow", campaign: c }) });
            if (c.status === "FINALIZADA") mobileMain.push({ label: "Duplicar", icon: <FileCopyOutlinedIcon fontSize="small" />, onClick: () => duplicate(c) });
            return mobileMain;
          })()
            .concat(menuItems(menu.campaign))
            .map((it) => (
              <MenuItem
                key={`m-${it.label}`}
                onClick={() => {
                  setMenu({ anchor: null, campaign: null });
                  it.onClick();
                }}
                style={it.danger ? { color: theme.palette.error.main } : undefined}
              >
                <ListItemIcon style={it.danger ? { color: "inherit" } : undefined}>{it.icon}</ListItemIcon>
                <ListItemText primary={it.label} />
              </MenuItem>
            ))}
        {!isMobile &&
          menuItems(menu.campaign).map((it) => (
            <MenuItem
              key={it.label}
              onClick={() => {
                setMenu({ anchor: null, campaign: null });
                it.onClick();
              }}
              style={it.danger ? { color: theme.palette.error.main } : undefined}
            >
              <ListItemIcon style={it.danger ? { color: "inherit" } : undefined}>{it.icon}</ListItemIcon>
              <ListItemText primary={it.label} />
            </MenuItem>
          ))}
      </Menu>

      <div className={classes.root}>
        <div className={classes.header}>
          <div className={classes.headerTitle}>
            <Title>
              Campanhas
              {totalCampaigns > 0 && (
                <span style={{ fontSize: 15, fontWeight: 500, color: theme.palette.text.secondary }}> · {totalCampaigns}</span>
              )}
            </Title>
            {!isMobile && (
              <Typography variant="body2" className={classes.muted}>
                Envios em massa pelo WhatsApp para listas de contatos ou tags.
              </Typography>
            )}
          </div>
          <div className={classes.headerButtons}>
            {!isMobile && (
              <Button
                variant="outlined"
                className={`${classes.headerButton} ${classes.secondaryButton}`}
                startIcon={<SpeedIcon />}
                onClick={() => history.push("/campaigns-config")}
              >
                Ritmo de envio
              </Button>
            )}
            <Button
              variant="contained"
              color="primary"
              disableElevation
              className={classes.headerButton}
              startIcon={<AddIcon />}
              onClick={() => setModal({ open: true, id: null })}
            >
              Nova campanha
            </Button>
          </div>
        </div>

        {sending && sending.allowed === false && (
          <Alert
            severity="info"
            style={{ marginBottom: 14, borderRadius: 12 }}
            action={
              <Button color="primary" size="small" onClick={() => history.push("/subscription")}>
                Ver assinatura
              </Button>
            }
          >
            {sending.message}
          </Alert>
        )}
        <KpiStrip items={kpis} classes={classes} theme={theme} loading={!summary} />

        <div className={classes.tabs} role="tablist" aria-label="Situação das campanhas">
          {STATUS_TABS.map((t) => {
            const n = t.key ? sc[t.key] || 0 : totalCampaigns;
            const active = statusFilter === t.key;
            return (
              <Button
                key={t.key || "all"}
                role="tab"
                aria-selected={active}
                className={`${classes.tab} ${active ? classes.tabActive : ""}`}
                onClick={() => setStatusFilter(t.key)}
              >
                {t.label}
                {summary && <span className={classes.tabCount}>{n}</span>}
              </Button>
            );
          })}
        </div>

        <Paper className={classes.mainPaper} variant="outlined">
          <div className={classes.toolbar} style={isMobile ? { padding: theme.spacing(1.5) } : undefined}>
            <TextField
              className={classes.searchField}
              variant="outlined"
              size="small"
              placeholder="Buscar por nome da campanha"
              type="search"
              value={searchParam}
              onChange={(e) => setSearchParam(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
              inputProps={{ "aria-label": "Buscar campanha" }}
            />
            {whatsapps.length > 1 && (
              <div className={`${classes.chipGroup} ${isMobile ? classes.chipGroupScroll : ""}`} role="group" aria-label="Conexão">
                {[{ id: "", name: "Todas as conexões" }, ...whatsapps].map((w) => {
                  const active = String(whatsappFilter) === String(w.id);
                  return (
                    <Chip
                      key={w.id || "all"}
                      clickable
                      className={classes.filterChip}
                      label={w.name}
                      color={active ? "primary" : "default"}
                      variant={active ? "default" : "outlined"}
                      aria-pressed={active}
                      onClick={() => setWhatsappFilter(w.id)}
                    />
                  );
                })}
              </div>
            )}
          </div>

          <div className={classes.tableWrapper} onScroll={handleScroll}>
            {isMobile ? (
              <div>
                {!loading && campaigns.length === 0 && (
                  <div className={classes.emptyState}>
                    <EventAvailableIcon className={classes.emptyIcon} />
                    <Typography variant="body2">{emptyMessage}</Typography>
                  </div>
                )}
                {campaigns.map((c) => {
                  const n = campaignNumbers(c);
                  const w = whenInfo(c);
                  const showBar = ["EM_ANDAMENTO", "CANCELADA", "PAUSADA_SEM_CREDITO", "FINALIZADA"].includes(c.status);
                  return (
                    <div key={c.id} className={classes.mobileRow}>
                      <div className={classes.mobileInfo} onClick={() => (c.status === "INATIVA" ? openEdit(c) : openReport(c))}>
                        <div className={classes.mobileTitle}>{c.name}</div>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4, flexWrap: "wrap" }}>
                          <CampaignStatusPill status={c.status} classes={classes} />
                          <span className={classes.subtle}>
                            {showBar ? `${n.delivered} de ${n.valid} · ${n.pct}%` : c.status === "INATIVA" ? missingStep(c) || "Pronta para agendar" : w.main}
                          </span>
                        </div>
                        {showBar && (
                          <ProgressBar
                            classes={classes}
                            height={4}
                            segments={[{ pct: n.pct, color: toneColor(theme, c.status === "PAUSADA_SEM_CREDITO" ? "red" : c.status === "CANCELADA" ? "amber" : c.status === "FINALIZADA" ? "purple" : "green") }]}
                          />
                        )}
                      </div>
                      <IconButton aria-label="Ações da campanha" onClick={(e) => setMenu({ anchor: e.currentTarget, campaign: c })}>
                        <MoreVertIcon />
                      </IconButton>
                    </div>
                  );
                })}
                {loading && <div className={classes.footer}>Carregando…</div>}
              </div>
            ) : (
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell className={classes.headCell} style={{ paddingLeft: 20 }}>Campanha</TableCell>
                    <TableCell className={classes.headCell}>Status</TableCell>
                    <TableCell className={classes.headCell} style={{ width: 220 }}>Progresso</TableCell>
                    <TableCell className={classes.headCell}>Quando</TableCell>
                    <TableCell className={classes.headCell} align="right" style={{ paddingRight: 20 }}>Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {campaigns.map((c) => {
                    const w = whenInfo(c);
                    return (
                      <TableRow key={c.id} className={classes.row}>
                        <TableCell style={{ paddingLeft: 20, maxWidth: 380 }}>
                          <button
                            type="button"
                            className={classes.campaignName}
                            onClick={() => (c.status === "INATIVA" ? openEdit(c) : openReport(c))}
                          >
                            {c.name}
                          </button>
                          <div className={classes.subtle} style={{ marginTop: 2 }}>{meta(c)}</div>
                        </TableCell>
                        <TableCell>
                          <CampaignStatusPill status={c.status} classes={classes} />
                        </TableCell>
                        <TableCell>{progressCell(c)}</TableCell>
                        <TableCell style={{ whiteSpace: "nowrap" }}>
                          <div>{w.main}</div>
                          {w.sub && <div className={classes.subtle}>{w.sub}</div>}
                        </TableCell>
                        <TableCell align="right" style={{ paddingRight: 12 }}>
                          <div className={classes.actions} style={{ gap: 6 }}>
                            {primaryActions(c)}
                            <IconButton
                              size="small"
                              aria-label="Mais ações"
                              className={classes.iconAction}
                              onClick={(e) => setMenu({ anchor: e.currentTarget, campaign: c })}
                            >
                              <MoreVertIcon fontSize="small" />
                            </IconButton>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {loading && <TableRowSkeleton columns={5} />}
                  {!loading && campaigns.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5}>
                        <div className={classes.emptyState}>
                          <EventAvailableIcon className={classes.emptyIcon} />
                          <Typography variant="body2">{emptyMessage}</Typography>
                          {!searchParam && !statusFilter && (
                            <Button color="primary" onClick={() => setModal({ open: true, id: null })}>
                              Criar a primeira campanha
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            )}
          </div>

          {campaigns.length > 0 && (
            <div className={classes.footer}>
              Mostrando {campaigns.length} de {count} {count === 1 ? "campanha" : "campanhas"}
              {hasMore ? " · role para carregar mais" : ""}
            </div>
          )}
        </Paper>
      </div>
    </MainContainer>
  );
};

export default Campaigns;
